import React, { useCallback, useEffect } from 'react';
import { Checkbox, Grid, TextField, Typography } from '@mui/material';
import {
    CheckCircle as CircleChecked,
    RadioButtonUnchecked as CircleUnchecked,
} from '@mui/icons-material';
import { partial, times } from 'lodash';
import useTaskField from '@dyl/pages/Tasks/edit/useTaskField';
import PropTypes from 'prop-types';
import { limitWordsAndSpaces } from '../utils/limitWordsAndSpaces';
import { useLevels } from '@dyl/systems/LevelsManager';
import { createOnPasteAtCaret } from '@dyl/utils/pasteAtCaret';

export default function TaskRadioOptionBlock({
    path = [''],
    fieldName = 'text',
    maxWords
}) {
    const { globalLevel } = useLevels();
    useEffect(() => {
        console.assert(!!path, 'No path specified!');
    }, [path]);

    const [array] = useTaskField(path);

    return (
        <Grid container spacing={2} style={{ flexDirection: 'column' }}>
            {times(array?.length, index => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                    <Item parent={path} index={index} fieldName={fieldName} globalLevel={globalLevel?.label} maxWords={maxWords} />
                </Grid>
            ))}
        </Grid>
    );
}
TaskRadioOptionBlock.propTypes = {
    path: PropTypes.array.isRequired,
    fieldName: PropTypes.string,
    maxWords: PropTypes.number
}

function onAnswerChange(setValue, maxWords, prevValue, globalLevel) {
    return (event) => {
        const value = limitWordsAndSpaces(prevValue, maxWords, event.target.value, globalLevel);
        setValue(value);
    };
}

function Item({ parent, index, fieldName = 'text', globalLevel, maxWords }) {
    const useField = partial(useTaskField, parent, index);

    const [, setItems] = useTaskField(parent);


    const [text, setText, textError] = useField(fieldName);

    const onTextChange = onAnswerChange(setText, maxWords, text, globalLevel);

    const [isCorrect] = useField('is_correct_answer');

    const setAsCorrect = useCallback(() => {
        setItems(prev => prev.map((a, i) => ({
            ...a,
            is_correct_answer: i === index,
        })));
    }, [index, setItems]);

    const letterId = String.fromCharCode('A'.charCodeAt(0) + index);

    const onPaste = useCallback(createOnPasteAtCaret(setText, text, maxWords), [text, setText, maxWords]);

    return (
        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
            <Checkbox
                checked={isCorrect}
                onClick={setAsCorrect}
                icon={<CircleUnchecked />}
                checkedIcon={<CircleChecked />}
                color='primary'
                style={{ alignItems: 'flex-start' }}
            />
            <div style={{ paddingTop: '8px' }}>
                <Typography sx={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }}>{`Option ${letterId}`}</Typography>
                <TextField
                    fullWidth
                    value={text}
                    variant='filled'
                    onChange={onTextChange}
                    onPaste={onPaste}
                    error={!!textError}
                    helperText={textError ?? 'Select if key'}
                    sx={{ width: '609px', '& .MuiInputBase-input': { width: '609px' }}}
                />
            </div>
        </div>
    );
}
Item.propTypes = {
    parent: PropTypes.any.isRequired,
    index: PropTypes.number.isRequired,
    fieldName: PropTypes.string,
    globalLevel: PropTypes.string,
    maxWords: PropTypes.number
}